import { type ButtonHTMLAttributes, forwardRef } from "react";
import { Link, type LinkProps } from "react-router-dom";

import styles from "./Button.module.css";
import { cx } from "../lib/cx";

export type ButtonSize = "default" | "lg" | "sm";
export type ButtonVariant =
  "accent" | "danger" | "default" | "ghost" | "primary";

type ButtonLook = {
  readonly className?: string;
  readonly icon?: boolean;
  readonly size?: ButtonSize;
  readonly variant?: ButtonVariant;
};

const buttonClass = ({
  className,
  icon = false,
  size = "default",
  variant = "default",
}: ButtonLook): string =>
  cx(
    styles.btn,
    variant === "primary" && styles.primary,
    variant === "accent" && styles.accent,
    variant === "ghost" && styles.ghost,
    variant === "danger" && styles.danger,
    size === "sm" && styles.sm,
    size === "lg" && styles.lg,
    icon && styles.icon,
    className,
  );

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & ButtonLook;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, icon, size, type = "button", variant, ...rest }, ref) => (
    <button
      className={buttonClass({ className, icon, size, variant })}
      ref={ref}
      type={type}
      {...rest}
    />
  ),
);

Button.displayName = "Button";

export type ButtonLinkProps = ButtonLook & LinkProps;

/**
 * A button-shaped link. Anything whose job is "go to this URL" renders one of
 * these rather than a `<Button onClick={nav…}>`, so the target is a real `href`
 * the browser can open in a new tab, copy, or middle-click.
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  ({ className, icon, size, variant, ...rest }, ref) => (
    <Link
      className={buttonClass({ className, icon, size, variant })}
      ref={ref}
      {...rest}
    />
  ),
);

ButtonLink.displayName = "ButtonLink";
